import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Calendar as CalendarIcon,
  FileText,
  MessageSquare,
} from 'lucide-react';

import {
  useAppointments,
  useGuidedSelfInjection,
  useThreads,
  type BookingKind,
} from '@/lib/api/hooks';
import { cn } from '@/lib/cn';
import { PageHeader } from '@/components/app-shell';
import { ArrowInline } from '@/lib/Icon';
import { ThreadPreview } from './components/ThreadPreview';
import { AppointmentRow } from './components/AppointmentRow';
import { BookingDialog } from './components/BookingDialog';
import { DocumentsTab } from './components/DocumentsTab';
import { GuidedSelfInjectionCard } from '@/features/guided-injection/components/GuidedSelfInjectionCard';
import { messageDrawer } from './stores/messageDrawer';
import type { Thread } from '@/types/api';
import { useCareTeamMessaging } from '@/features/messaging/messagingHub';
import { ClinicTeamCards } from '@/features/messaging/components/ClinicTeamCards';

type Tab = 'messages' | 'appointments' | 'documents';

export function mostRecentThread(threads: Thread[]): Thread | undefined {
  return threads.reduce<Thread | undefined>((latest, thread) => {
    if (!latest) return thread;
    return Date.parse(thread.last_message_at) >
      Date.parse(latest.last_message_at)
      ? thread
      : latest;
  }, undefined);
}

/**
 * ClinicScreen — mock-matched.
 *
 * Sunken header band: 'CLINIC' + ▶ caption. Three-segment tab toggle
 * (Messages / Appointments / Documents) — active segment is the darker
 * sunken tone with a bottom rule. Below: a full-width black primary CTA
 * (New message / Book appointment / Upload document) that opens the
 * dialog or dispatches inline. Tab body lists the relevant content.
 */
export function ClinicScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingKind, setBookingKind] = useState<BookingKind>('default');
  const careTeamMessaging = useCareTeamMessaging();

  // Deep-link: home-task CTAs land here with `?book=1` (+ optional
  // `&kind=peptide_consult` for the evaluation visit). Open the dialog
  // with that kind, then strip the params so a refresh doesn't re-open.
  useEffect(() => {
    if (searchParams.get('book') !== '1') return;
    setBookingKind(
      searchParams.get('kind') === 'peptide_consult'
        ? 'peptide_consult'
        : 'default',
    );
    setBookingOpen(true);
    const params = new URLSearchParams(searchParams);
    params.delete('book');
    params.delete('kind');
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  // One appointment at a time — hide the Book CTA when there's already a
  // scheduled visit on the calendar. Patient can reschedule or cancel
  // from the AppointmentRow; cancelling clears `scheduled` and the CTA
  // returns. Past + completed appts don't gate (you can always book the
  // next one).
  const { data: clinicAppts } = useAppointments();
  const hasScheduled = (clinicAppts ?? []).some(
    (a) => a.status === 'scheduled' && a.kind !== 'guided_self_injection',
  );

  const tabParam = searchParams.get('tab');
  const tab: Tab =
    tabParam === 'appointments'
      ? 'appointments'
      : tabParam === 'documents'
        ? 'documents'
        : 'messages';

  const setTab = (next: Tab) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'messages') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  // One conversation per patient: when a thread already exists the CTA
  // re-opens it instead of composing a second thread (shares MessagesTab's
  // query via react-query, so no extra fetch).
  const {
    data: ctaThreads,
    isFetching: threadsFetching,
    refetch: refetchThreads,
  } = useThreads();
  const existingThread = mostRecentThread(ctaThreads ?? []);
  const openingMessageRef = useRef(false);
  const [openingMessage, setOpeningMessage] = useState(false);
  const [messageOpenError, setMessageOpenError] = useState<string | null>(null);

  // Always refresh before deciding whether the patient needs a new Healthie
  // conversation. This closes the window where the CTA could be clicked while
  // the initial thread query was still loading (or while an empty cache entry
  // was stale) and accidentally create a parallel conversation.
  const openPatientConversation = useCallback(async () => {
    if (openingMessageRef.current) return;
    openingMessageRef.current = true;
    setOpeningMessage(true);
    setMessageOpenError(null);
    try {
      const result = await refetchThreads();
      if (result.error || !result.data) {
        setMessageOpenError('Messages could not be loaded. Please try again.');
        return;
      }
      const thread = mostRecentThread(result.data);
      if (thread) messageDrawer.openThread(thread.id);
      else messageDrawer.openNew();
    } finally {
      openingMessageRef.current = false;
      setOpeningMessage(false);
    }
  }, [refetchThreads]);

  // Account > Your clinician lands here with ?message=1. Consume the
  // one-shot intent, then let the same refreshed-thread path used by this
  // page open the canonical conversation.
  const shouldOpenMessage = searchParams.get('message') === '1';
  useEffect(() => {
    if (!shouldOpenMessage) return;
    const params = new URLSearchParams(searchParams);
    params.delete('message');
    setSearchParams(params, { replace: true });
    void openPatientConversation();
    // The boolean is the one-shot trigger. Including searchParams here would
    // re-run the effect when we remove it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldOpenMessage, openPatientConversation]);

  return (
    <div className="flex flex-col">
      <PageHeader
        title="Clinic"
        description={
          careTeamMessaging
            ? 'Message your providers or System Support, book appointments, and share documents.'
            : 'Message your clinician, book appointments, and share documents — all in one place.'
        }
      />

      <div className="flex flex-col gap-25">
        {/* Tab toggle — full-bleed accent strip, matches LearnScreen pattern. */}
        <div className="grid grid-cols-3 p-2 pb-0">
          <SegmentBtn
            active={tab === 'messages'}
            onClick={() => setTab('messages')}
          >
            Messages
          </SegmentBtn>
          <SegmentBtn
            active={tab === 'appointments'}
            onClick={() => setTab('appointments')}
          >
            Appointments
          </SegmentBtn>
          <SegmentBtn
            active={tab === 'documents'}
            onClick={() => setTab('documents')}
          >
            Documents
          </SegmentBtn>
        </div>

        <div className="flex flex-col gap-25 px-15 md:px-40 max-w-[65rem] mx-auto w-full">
          {tab === 'messages' && careTeamMessaging && <ClinicTeamCards />}
          {tab === 'messages' && !careTeamMessaging && (
            <div data-tour="clinic-composer">
              <PrimaryCTA
                onClick={() => void openPatientConversation()}
                disabled={
                  openingMessage ||
                  (ctaThreads === undefined && threadsFetching)
                }
              >
                {openingMessage || (ctaThreads === undefined && threadsFetching)
                  ? 'Loading messages…'
                  : existingThread
                    ? 'Send a message'
                    : 'New message'}
              </PrimaryCTA>
              {messageOpenError && (
                <p role="alert" className="mt-8 text-13 text-danger">
                  {messageOpenError}
                </p>
              )}
            </div>
          )}
          {tab === 'appointments' && !hasScheduled && (
            <PrimaryCTA onClick={() => setBookingOpen(true)}>
              Book appointment
            </PrimaryCTA>
          )}
          {tab === 'documents' && (
            <PrimaryCTA
              onClick={() => {
                // Documents upload is wired inside DocumentsTab — dispatch a
                // synthetic click on the hidden file input there. The event
                // keeps this CTA decoupled from the tab body.
                window.dispatchEvent(new CustomEvent('clinic:open-upload'));
              }}
            >
              Upload document
            </PrimaryCTA>
          )}

          {tab === 'messages' ? (
            careTeamMessaging ? null : <MessagesTab />
          ) : tab === 'appointments' ? (
            <AppointmentsTab />
          ) : (
            <DocumentsTab />
          )}
        </div>
      </div>

      <BookingDialog
        open={bookingOpen}
        onOpenChange={(next) => {
          setBookingOpen(next);
          if (!next) setBookingKind('default');
        }}
        kind={bookingKind}
      />
    </div>
  );
}

function SegmentBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'h-40 type-body-md transition-colors',
        active
          ? 'bg-[var(--accent)] text-white'
          : 'bg-[var(--accent-hover)] text-white',
      )}
    >
      {children}
    </button>
  );
}

function PrimaryCTA({
  onClick,
  disabled = false,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full flex items-center justify-between h-50 px-15 bg-black text-white font-body text-15 hover:bg-[#252525] disabled:cursor-wait disabled:opacity-60"
    >
      <span>{children}</span>
      <ArrowInline />
    </button>
  );
}

function MessagesTab() {
  const { data, isLoading } = useThreads();
  if (isLoading)
    return (
      <div className="h-[20rem] bg-[var(--surface-sunken)] animate-pulse" />
    );
  const threads = data ?? [];
  if (threads.length === 0) return <Empty kind="messages" />;
  return (
    <div className="flex flex-col gap-10">
      {threads.map((t) => (
        <ThreadPreview key={t.id} thread={t} />
      ))}
    </div>
  );
}

function AppointmentsTab() {
  const { data, isLoading } = useAppointments();
  const guided = useGuidedSelfInjection();
  if (isLoading)
    return (
      <div className="h-[20rem] bg-[var(--surface-sunken)] animate-pulse" />
    );
  // Guided injection sessions have a dedicated entitlement surface with
  // provider-bound reschedule/cancel endpoints. Never feed one into the
  // ordinary AppointmentRow/BookingDialog, which is the Dr. Hinds sync rail.
  const appts = (data ?? []).filter(
    (appointment) => appointment.kind !== 'guided_self_injection',
  );
  const upcoming = appts.filter((a) => a.status === 'scheduled');
  const past = appts.filter((a) => a.status !== 'scheduled');
  const guidedState = guided.data;
  const showGuided = Boolean(
    guidedState?.status &&
    (guidedState.entitled || guidedState.status === 'refunded') &&
    guidedState.experiment &&
    guidedState.presentation &&
    guidedState.preparation,
  );
  return (
    <div className="flex flex-col gap-25">
      {guided.isLoading && (
        <div className="h-[14rem] animate-pulse bg-[var(--surface-sunken)]" />
      )}
      {showGuided &&
        guidedState?.status &&
        guidedState.experiment &&
        guidedState.presentation &&
        guidedState.preparation && (
          <GuidedSelfInjectionCard
            state={{
              ...guidedState,
              status: guidedState.status,
              experiment: guidedState.experiment,
              presentation: guidedState.presentation,
              preparation: guidedState.preparation,
            }}
          />
        )}
      {upcoming.length === 0 && past.length === 0 && !showGuided ? (
        <Empty kind="appointments" />
      ) : (
        <>
          {upcoming.length > 0 && (
            <section className="flex flex-col gap-10">
              {upcoming.map((a) => (
                <AppointmentRow key={a.id} appt={a} />
              ))}
            </section>
          )}
          {past.length > 0 && (
            <section className="flex flex-col gap-10">
              {past.map((a) => (
                <AppointmentRow key={a.id} appt={a} />
              ))}
            </section>
          )}
        </>
      )}
    </div>
  );
}

function Empty({ kind }: { kind: 'messages' | 'appointments' }) {
  const IconCmp = kind === 'messages' ? MessageSquare : CalendarIcon;
  return (
    <div className="border border-[#ECECEC]">
      <div className="flex items-center justify-between px-15 py-10 bg-[var(--surface-sunken)]">
        <IconCmp size={20} strokeWidth={1.5} className="text-[#7C7C7C]" />
        <div className="flex-1 font-mono text-13 uppercase text-[#7C7C7C] text-right">
          {kind === 'messages' ? 'No messages yet' : 'No appointments yet'}
        </div>
      </div>
      <div className="font-body italic border-t border-[#ECECEC] text-13 text-[#7C7C7C] max-w-[44ch] mx-auto leading-135 p-15">
        {kind === 'messages'
          ? 'Start a thread with your clinician — questions about your protocol, side effects, or scheduling.'
          : 'Book a visit with your clinician to review your protocol.'}
      </div>
    </div>
  );
}

// Silence the unused FileText import — kept for future "no documents"
// empty state once the DocumentsTab refactor lands.
void FileText;
